/**
 * useSlotPreSelection Hook
 * Slot pre-selection logic from URL parameters
 */

import { useEffect } from 'react';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { AvailableDay, BookingData, BookingStep, TimeSlot } from '../utils/bookingConstants.js';

export function useSlotPreSelection(
  searchParams: ReadonlyURLSearchParams | null,
  availableDays: AvailableDay[] | undefined,
  bookingData: BookingData,
  currentStep: BookingStep,
  eventType: string | null,
  onSlotSelect: (slot: TimeSlot) => void,
  onStepChange: (step: BookingStep) => void
) {
  useEffect(() => {
    const slotParam = searchParams?.get('slot');
    const eventTypeParam = searchParams?.get('eventType');

    // Only pre-select slot if:
    // 1. There's a slot param in URL
    // 2. We have available days
    // 3. We don't already have a selected slot OR the selected slot doesn't match the URL param
    // 4. We're not already past slot selection (to avoid resetting progress)
    const currentSlotStart = bookingData.selectedSlot?.start;
    const isPastSlotSelection =
      currentStep === 'booking-details' || currentStep === 'success';
    const shouldPreSelect =
      slotParam &&
      availableDays &&
      availableDays.length > 0 &&
      (!bookingData.selectedSlot || currentSlotStart !== slotParam) &&
      !isPastSlotSelection;

    if (shouldPreSelect) {
      // Find the matching slot in the available days
      for (const day of availableDays) {
        const matchingSlot = day.slots.find((slot) => slot.start === slotParam);
        if (matchingSlot) {
          // Only update if the slot is different from what we have
          if (!bookingData.selectedSlot || bookingData.selectedSlot.start !== slotParam) {
            onSlotSelect(matchingSlot);

            // If we have a slot and either eventType or bookeeEmail, advance.
            // Only set step if we're still on select-slot (don't override if
            // the user already moved on). `booking-details` now carries the
            // review + confirm action, so it is the single next stop whether or
            // not name/email arrived in the URL.
            if ((eventTypeParam || bookingData.bookeeEmail) && currentStep === 'select-slot') {
              onStepChange('booking-details');
            }
          }
          break;
        }
      }
    }
  }, [
    availableDays?.length,
    searchParams?.get('slot'),
    bookingData.selectedSlot,
    bookingData.bookeeEmail,
    bookingData.name,
    bookingData.email,
    searchParams?.get('eventType'),
    currentStep,
    onSlotSelect,
    onStepChange,
    bookingData,
  ]);
}
